import { getCredential, getEvalVendorOverride, refreshCredential } from './credentials'
import { SpeechError, SpeechErrorCode } from './errors'
import { assess as azureAssess } from './eval/azure'
import { assess as speechsuperAssess } from './eval/speechsuper'

/** 端侧打包了哪几家评测适配器——平台只会在这个列表里选 */
export const SUPPORTED_EVAL_VENDORS = ['azure', 'speechsuper']

const EVAL_ADAPTERS = { azure: azureAssess, speechsuper: speechsuperAssess }

function pickAdapter(table, vendor, capability) {
  const adapter = table[vendor]
  if (!adapter) {
    // 平台可能新增厂商，端侧没打包对应适配器时给出明确错误而不是崩在下游
    throw new SpeechError(
      SpeechErrorCode.CREDENTIAL_FAILED,
      `本端未实现 ${capability} 厂商 ${vendor} 的适配器`,
      { vendor, capability }
    )
  }
  return adapter
}

/**
 * 取凭证 → 调厂商；厂商拒绝凭证（401/403）时刷新一次凭证重试。
 * 刷新接口有 30 秒节流，所以只重试一次，绝不循环。
 */
async function runWithCredential({ capability, supportedVendors, adapters, invoke }) {
  let credential = await getCredential({ capability, supportedVendors })
  try {
    const adapter = pickAdapter(adapters, credential.vendor, capability)
    return { credential, result: await invoke(adapter, credential) }
  } catch (e) {
    if (!(e instanceof SpeechError) || !e.shouldRefreshCredential) throw e

    credential = await refreshCredential({ capability, supportedVendors })
    const adapter = pickAdapter(adapters, credential.vendor, capability)
    return { credential, result: await invoke(adapter, credential) }
  }
}

/** 本轮评测要向后端声明的厂商列表：开发期可覆盖，正式环境恒为整份白名单 */
export function evalSupportedVendors() {
  return getEvalVendorOverride() || SUPPORTED_EVAL_VENDORS
}

/**
 * 发音测评。厂商由后端按环境下发（国内 azure / 海外 speechsuper），
 * 开发期可用 getEvalVendorOverride 强制指定，正式环境该覆盖恒为空。
 *
 * @param {Blob} wav 16kHz/16bit/mono
 * @param {string} refText 参考文本
 * @param {'word'|'sentence'|'paragraph'} category
 * @returns {Promise<{credential: object, result: object}>}
 */
export function assess({ wav, refText, category, timeoutMs }) {
  return runWithCredential({
    capability: 'eval',
    supportedVendors: evalSupportedVendors(),
    adapters: EVAL_ADAPTERS,
    invoke: (adapter, credential) => adapter({ wav, refText, category, credential, timeoutMs }),
  })
}

/**
 * 题目的评测类型 → 厂商粒度。
 *
 * 题库沿用旧引擎的四种 category（read_word / read_syllable / read_sentence / read_chapter），
 * 厂商侧只分单字、句子、段落三档；没标类型时按参考文本长度兜底。
 */
export function categoryOf(category, refText) {
  if (category === 'read_word' || category === 'read_syllable') return 'word'
  if (category === 'read_sentence') return 'sentence'
  if (category === 'read_chapter') return 'paragraph'
  const length = (refText || '').replace(/\s/g, '').length
  if (length < 3) return 'word'
  if (length <= 400) return 'sentence'
  return 'paragraph'
}

export { SpeechError, SpeechErrorCode } from './errors'
